import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Upload, FileText, X, CheckCircle2, CalendarDays, User, PenLine } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { COMPANY, US_STATES } from "../constants/company";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STEPS = ["Your Info", "Team Member", "Date & Time", "Documents", "Signature"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOC_CATEGORIES = [
  { value: "application", label: "Application Form" },
  { value: "w2", label: "W-2" },
  { value: "1099", label: "1099" },
  { value: "id", label: "Photo ID" },
  { value: "prior_return", label: "Prior Year Return" },
  { value: "other", label: "Other Document" },
];
const today = () => new Date().toISOString().slice(0, 10);

export default function PublicBooking() {
  const [step, setStep] = useState(0);
  const [team, setTeam] = useState([]);
  const [info, setInfo] = useState({ name: "", email: "", phone: "", notes: "" });
  const [memberId, setMemberId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("application");
  const [stateFilter, setStateFilter] = useState("all");
  const [sig, setSig] = useState({ fullName: "", dataUrl: null, agreed: false });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/public/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSlots(null);
    setSlot("");
    if (memberId && date) {
      api.get(`/public/availability?team_member_id=${memberId}&date=${date}`)
        .then((r) => setSlots(r.data))
        .catch(() => setSlots({ slots: [], closed: false }));
    }
  }, [memberId, date]);

  const setI = (k) => (e) => setInfo((i) => ({ ...i, [k]: e.target.value }));
  const selectedMember = team.find((m) => m.user_id === memberId);

  const stageFiles = (e) => {
    const picked = Array.from(e.target.files || []).map((f) => ({ file: f, category }));
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const canNext = () => {
    if (step === 0) return info.name.trim() && /.+@.+\..+/.test(info.email);
    if (step === 1) return !!memberId;
    if (step === 2) return !!(memberId && date && slot);
    if (step === 3) return true;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/public/bookings", {
        ...info, team_member_id: memberId, date, time_slot: slot,
        signature: { fullName: sig.fullName, dataUrl: sig.dataUrl },
      });
      for (const item of files) {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("category", item.category);
        await api.post(`/public/bookings/${booking.booking_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setConfirmed(booking);
      toast.success("Appointment request sent — you'll get an email once the team confirms");
    } catch (err) {
      toast.error(formatApiError(err, "Booking failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="booking-confirmed-page">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <Card className="max-w-lg w-full animate-fade-up">
            <CardContent className="p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
              <h1 className="mt-5 text-3xl font-heading font-semibold tracking-tight">Request received!</h1>
              <p className="mt-3 text-muted-foreground text-sm">
                {confirmed.date} at {confirmed.time_slot} with {confirmed.team_member_name}.
                Our team will review their calendar and email you once your appointment is confirmed.
              </p>
              <div className="mt-6 bg-muted/50 border border-border rounded-md p-4 text-sm text-left space-y-1">
                <p><span className="text-muted-foreground">Office:</span> {COMPANY.fullAddress}</p>
                <p><span className="text-muted-foreground">Phone:</span> {COMPANY.phone}</p>
                {files.length > 0 && <p><span className="text-muted-foreground">Documents:</span> {files.length} file(s) received</p>}
              </div>
              <Button asChild className="mt-8 w-full">
                <Link to="/" data-testid="booking-done-home-button">Back to Home</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="public-booking-page">
      <Header />
      <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-12">
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Book online</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">Schedule Your Appointment</h1>
        <p className="mt-2 text-sm text-muted-foreground">Pick a time, upload your documents, and sign your intake paperwork — no account needed.</p>

        <div className="flex gap-1.5 mt-8 mb-8" data-testid="pb-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 text-left">
              <div className={`h-1.5 rounded-full transition-colors duration-200 ${i <= step ? "bg-primary" : "bg-border"}`} />
              <p className={`text-[10px] mt-1.5 hidden sm:block ${i === step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</p>
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            {step === 0 && (
              <div className="space-y-4" data-testid="pb-step-info">
                <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><User className="w-5 h-5 text-primary" /> Your Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Full name *</Label><Input data-testid="pb-name-input" value={info.name} onChange={setI("name")} placeholder="Jane Doe" /></div>
                  <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="pb-phone-input" type="tel" value={info.phone} onChange={setI("phone")} placeholder="(385) 555-0100" /></div>
                </div>
                <div className="space-y-1.5"><Label>Email *</Label><Input data-testid="pb-email-input" type="email" value={info.email} onChange={setI("email")} placeholder="you@example.com" /></div>
                <div className="space-y-1.5"><Label>What do you need help with? (optional)</Label><Textarea data-testid="pb-notes-input" rows={2} value={info.notes} onChange={setI("notes")} placeholder="New business formation, personal return, bookkeeping…" /></div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="pb-step-member">
                <h2 className="font-heading text-2xl font-semibold">Choose a Team Member</h2>
                <div className="space-y-1.5 max-w-xs">
                  <Label>Find a tax pro by state</Label>
                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger data-testid="pb-state-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="pb-state-all">All locations</SelectItem>
                      {US_STATES.map((s) => <SelectItem key={s} value={s} data-testid={`pb-state-${s}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {team.filter((m) => stateFilter === "all" || m.state === stateFilter).map((m) => (
                    <button key={m.user_id} type="button" onClick={() => setMemberId(m.user_id)} data-testid={`pb-member-${m.user_id}`}
                      className={`text-left border rounded-md p-5 transition-colors duration-200 ${memberId === m.user_id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                      {m.photo_url ? (
                        <img src={`${BACKEND}${m.photo_url}`} alt={m.name} className="w-16 h-16 rounded-md object-cover mb-3" data-testid={`pb-member-photo-${m.user_id}`} />
                      ) : (
                        <div className="w-16 h-16 rounded-md bg-primary/10 flex items-center justify-center font-heading text-2xl font-semibold text-primary mb-3">
                          {m.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <p className="font-heading text-xl font-semibold">{m.name}</p>
                      {m.title && <p className="text-xs text-primary font-medium mt-0.5">{m.title}</p>}
                      {m.office && <p className="text-xs text-muted-foreground mt-1">{m.office}{m.state ? ` · ${m.state}` : ""}</p>}
                    </button>
                  ))}
                  {team.filter((m) => stateFilter === "all" || m.state === stateFilter).length === 0 && (
                    <p className="text-sm text-muted-foreground sm:col-span-2" data-testid="pb-no-members-state">No tax pros in this state yet — try "All locations".</p>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="pb-step-datetime">
                <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><CalendarDays className="w-5 h-5 text-primary" /> Pick a Date & Time</h2>
                <div className="space-y-1.5 max-w-xs">
                  <Label>Date</Label>
                  <Input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="pb-date-input" />
                </div>
                {slots?.closed && <p className="text-sm text-destructive" data-testid="pb-closed-message">Closed on this day — please pick another date.</p>}
                {slots && !slots.closed && (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="pb-slots-grid">
                    {slots.slots.map((s) => (
                      <button key={s.time} type="button" disabled={!s.available} onClick={() => setSlot(s.time)}
                        data-testid={`pb-slot-${s.time.replace(":", "")}`}
                        className={`text-xs py-2 rounded-md border transition-colors duration-200 ${
                          slot === s.time ? "bg-primary text-primary-foreground border-primary"
                          : s.available ? "border-border hover:bg-secondary/40"
                          : "border-border text-muted-foreground/40 line-through cursor-not-allowed"}`}>
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
                {!slots && <p className="text-sm text-muted-foreground">Select a date to see available times with {selectedMember?.name}.</p>}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="pb-step-documents">
                <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><Upload className="w-5 h-5 text-primary" /> Upload Documents</h2>
                <p className="text-sm text-muted-foreground">Application forms, W-2s, 1099s, photo ID, or prior returns (PDF, Word, JPG/PNG — max 15MB each). Optional but speeds up your visit.</p>
                <div className="flex gap-3 items-end">
                  <div className="space-y-1.5 flex-1">
                    <Label>Document type</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="pb-doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="pb-add-file-button">
                    <Upload className="w-4 h-4 mr-1" /> Add file
                  </Button>
                  <input ref={fileRef} type="file" multiple className="hidden" data-testid="pb-file-input"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.heic" onChange={stageFiles} />
                </div>
                <div className="space-y-2" data-testid="pb-file-list">
                  {files.length === 0 && <p className="text-sm text-muted-foreground">No files added yet.</p>}
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`pb-file-${i}`}>
                      <FileText className="w-4 h-4 text-primary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{f.file.name}</p>
                        <p className="text-xs text-muted-foreground">{DOC_CATEGORIES.find((c) => c.value === f.category)?.label} · {(f.file.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button type="button" onClick={() => setFiles((prev) => prev.filter((_, x) => x !== i))} data-testid={`pb-file-remove-${i}`}>
                        <X className="w-4 h-4 text-muted-foreground hover:text-destructive transition-colors" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4" data-testid="pb-step-signature">
                <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><PenLine className="w-5 h-5 text-primary" /> Sign Your Intake Paperwork</h2>
                <div className="bg-muted/50 border border-border rounded-md p-4 text-sm space-y-1" data-testid="pb-review-summary">
                  <p><span className="text-muted-foreground">Name:</span> {info.name}</p>
                  <p><span className="text-muted-foreground">Appointment:</span> {date} at {slot} with {selectedMember?.name}</p>
                  <p><span className="text-muted-foreground">Documents:</span> {files.length} file(s) attached</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  By signing, you authorize {COMPANY.name} to begin your tax intake using the information and documents provided, and certify everything is accurate to the best of your knowledge.
                </p>
                <div className="space-y-1.5">
                  <Label>Type your full legal name</Label>
                  <Input data-testid="pb-signature-name" value={sig.fullName} onChange={(e) => setSig((s) => ({ ...s, fullName: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Sign below</Label>
                  <SignaturePad onChange={(dataUrl) => setSig((s) => ({ ...s, dataUrl }))} />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="pb-consent-label">
                  <Checkbox checked={sig.agreed} onCheckedChange={(v) => setSig((s) => ({ ...s, agreed: !!v }))} data-testid="pb-consent-checkbox" />
                  <span className="text-sm text-muted-foreground">I agree to the terms above and consent to sign electronically.</span>
                </label>
              </div>
            )}

            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="pb-back-button">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="pb-next-button">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={submit} disabled={submitting || !sig.agreed || !sig.fullName || !sig.dataUrl} data-testid="pb-submit-button">
                  {submitting ? "Submitting…" : "Confirm & Sign"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="pb-logo-link" className="flex items-center">
          <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-11 w-auto rounded-sm" />
        </Link>
        <div className="flex items-center gap-3 text-sm">
          <a href={COMPANY.phoneHref} data-testid="pb-call-link" className="hidden sm:block text-muted-foreground hover:text-foreground transition-colors">{COMPANY.phone}</a>
          <Button asChild size="sm" variant="outline">
            <Link to="/auth" data-testid="pb-login-link">Client Login</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
